import {
  SESSION_TTL_MS,
  NEVER_CACHE,
  MEMORY_ONLY,
  PERSISTENT,
  PORTAL_COLLECTIONS,
  MAX_CACHE_SIZE,
  CACHE_PREFIX
} from './cacheConfig';

// ─── SIMPLE OBFUSCATION ────────────────────────────────────────────────
// NOT encryption — prevents casual DevTools reading of IndexedDB.
// Genuine security comes from Firestore rules + 8h TTL + NEVER_CACHE.

function obfuscate(str) {
  try {
    return btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g,
        (_, p1) => String.fromCharCode('0x' + p1))
    );
  } catch {
    return str;
  }
}

function deobfuscate(str) {
  try {
    return decodeURIComponent(
      Array.from(atob(str))
        .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
  } catch {
    return str;
  }
}

// ─── IN-MEMORY STORE ───────────────────────────────────────────────────
const memoryStore = new Map();

// ─── INDEXED DB ────────────────────────────────────────────────────────
const DB_NAME    = 'spacemancraft_cache';
const DB_VERSION = 1;
const STORE_NAME = 'collections';
let dbPromise = null;

function getDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (!window.indexedDB) { resolve(null); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    req.onsuccess  = e => resolve(e.target.result);
    req.onerror    = () => { dbPromise = null; resolve(null); };
  });
  return dbPromise;
}

async function idbGet(key) {
  const db = await getDB();
  if (!db) return null;
  return new Promise(resolve => {
    const tx  = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = e => resolve(e.target.result || null);
    req.onerror   = () => resolve(null);
  });
}

async function idbSet(key, value) {
  const db = await getDB();
  if (!db) return;
  return new Promise(resolve => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({ key, ...value });
    tx.oncomplete = resolve;
    tx.onerror    = resolve;
  });
}

async function idbDelete(key) {
  const db = await getDB();
  if (!db) return;
  return new Promise(resolve => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = resolve;
    tx.onerror    = resolve;
  });
}

async function idbClear() {
  const db = await getDB();
  if (!db) return;
  return new Promise(resolve => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = resolve;
    tx.onerror    = resolve;
  });
}

// ─── HELPERS ───────────────────────────────────────────────────────────
function makeCacheKey(col, uid, queryHash = '') {
  return `${CACHE_PREFIX}${uid}_${col}${queryHash ? '_' + queryHash : ''}`;
}

function isExpired(timestamp) {
  return !timestamp || (Date.now() - timestamp) > SESSION_TTL_MS;
}

// ─── PUBLIC API ────────────────────────────────────────────────────────
export const SessionCache = {

  strategy(col) {
    if (NEVER_CACHE.has(col))       return 'none';
    if (MEMORY_ONLY.has(col))       return 'memory';
    if (PORTAL_COLLECTIONS.has(col)) return 'memory';
    if (PERSISTENT.has(col))        return 'persistent';
    return 'memory';
  },

  async get(col, uid, queryHash = '') {
    const strategy = this.strategy(col);
    if (strategy === 'none') return null;

    const key = makeCacheKey(col, uid, queryHash);

    const mem = memoryStore.get(key);
    if (mem) {
      if (isExpired(mem.timestamp) || mem.uid !== uid) {
        memoryStore.delete(key);
        return null;
      }
      return mem.data;
    }

    if (strategy === 'persistent') {
      const idb = await idbGet(key);
      if (idb) {
        if (isExpired(idb.timestamp) || idb.uid !== uid) {
          await idbDelete(key);
          return null;
        }
        try {
          const data = JSON.parse(deobfuscate(idb.payload));
          memoryStore.set(key, { data, timestamp: idb.timestamp, uid });
          return data;
        } catch {
          await idbDelete(key);
          return null;
        }
      }
    }

    return null;
  },

  async set(col, uid, data, queryHash = '') {
    const strategy = this.strategy(col);
    if (strategy === 'none') return;

    const limited   = Array.isArray(data) ? data.slice(0, MAX_CACHE_SIZE) : data;
    const key       = makeCacheKey(col, uid, queryHash);
    const timestamp = Date.now();

    memoryStore.set(key, { data: limited, timestamp, uid });

    if (strategy === 'persistent') {
      try {
        const payload = obfuscate(JSON.stringify(limited));
        await idbSet(key, { timestamp, uid, payload });
      } catch {
        // IndexedDB write failure is non-blocking
      }
    }
  },

  async invalidate(col, uid, queryHash = '') {
    const key = makeCacheKey(col, uid, queryHash);
    memoryStore.delete(key);
    await idbDelete(key);
  },

  // Invalidate all cached entries for a collection regardless of uid.
  // Used after pin toggles so any user's warm cache is cleared.
  async invalidateCollection(col) {
    const suffix = `_${col}`;
    for (const key of [...memoryStore.keys()]) {
      if (key.includes(suffix)) memoryStore.delete(key);
    }
    // IDB entries are cleared opportunistically on next get() via TTL check;
    // a full IDB scan on every pin would be slow, so we skip it here.
  },

  async clearAll(uid) {
    for (const [key, entry] of memoryStore.entries()) {
      if (!uid || entry?.uid === uid) memoryStore.delete(key);
    }
    await idbClear();
  },

  async sweepExpired() {
    for (const [key, entry] of memoryStore.entries()) {
      if (isExpired(entry.timestamp)) memoryStore.delete(key);
    }
    const db = await getDB();
    if (!db) return;
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.openCursor();
    req.onsuccess = e => {
      const cursor = e.target.result;
      if (!cursor) return;
      if (isExpired(cursor.value.timestamp)) cursor.delete();
      cursor.continue();
    };
  },

  // Expose memory store size for dev badge
  memSize() { return memoryStore.size; },
};
